import { memo, useCallback, useMemo, useState } from 'react';
import { Button, Flex, Input, Popover, Space, Tooltip, Typography } from 'antd';
import { OrderedListOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { buildSequentialCodes, DEFAULT_TEXT_MAX_LENGTH_SHORT } from '@pmp/utils';
import { SequentialCodeControlProps } from '../interfaces';
import styles from '../styles.module.css';

const { Text } = Typography;

export const SequentialCodeControl = memo(({ rowCount, onApply }: SequentialCodeControlProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [startCode, setStartCode] = useState('');

  const codes = useMemo(() => buildSequentialCodes(startCode, rowCount), [startCode, rowCount]);

  const handleApply = useCallback(() => {
    if (!codes) return;
    onApply(startCode.trim());
    setOpen(false);
    setStartCode('');
  }, [codes, onApply, startCode]);

  const content = (
    <Space direction="vertical" size="small" className={styles.sequentialCodeContent}>
      <Text>{t('global.labels.sequentialCodeDescription', { rows: rowCount })}</Text>
      <Input
        value={startCode}
        onChange={event => setStartCode(event.target.value)}
        onPressEnter={handleApply}
        maxLength={DEFAULT_TEXT_MAX_LENGTH_SHORT}
        placeholder={t('global.labels.sequentialCodePlaceholder')}
        aria-label={t('global.labels.sequentialCode')}
      />
      {codes && (
        <Text type="secondary">{t('global.labels.sequentialCodePreview', { first: codes[0], last: codes[codes.length - 1] })}</Text>
      )}
      <Flex justify="flex-end">
        <Button type="primary" size="small" disabled={!codes} onClick={handleApply}>
          {t('enso.buttons.apply')}
        </Button>
      </Flex>
    </Space>
  );

  return (
    <Popover trigger="click" open={open} onOpenChange={setOpen} placement="bottom" content={content} destroyOnHidden>
      <Tooltip title={t('global.labels.sequentialCode')} placement="top">
        <Button type="text" icon={<OrderedListOutlined />} aria-label={t('global.labels.sequentialCode')} />
      </Tooltip>
    </Popover>
  );
});
